/* Workspace Sub-account detail — live tally, registrations, QR share, admin tools */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import {
  Share2, Copy, Download, Plus, Pencil, UserPlus, Lock, ChevronRight, QrCode, CircleDollarSign, Send, Printer, Trash2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { API } from '../../utils/api';
import { GuestDetailsSwitch } from '../../components/GuestDetails';
import { downloadFlyer } from '../../utils/flyer';
import { wsErr, downloadCsv } from './workspaceUi';
import { fmtKr, fmtDate } from '../owner/ownerUi';

export function WsSub({ org, sub, t, lang, headers, onOpenSub }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [dialog, setDialog] = useState(null); // 'new_event' | 'edit' | 'moderator' | null
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pool, setPool] = useState(null);

  const orgId = org.org_id;
  const subId = sub.id;

  const load = useCallback(async () => {
    try {
      setErr(false);
      const res = await axios.get(`${API}/org/${orgId}/scope/sub-accounts/${subId}/live`, { headers });
      setData(res.data);
    } catch (e) { console.error(e); setErr(true); }
  }, [orgId, subId, headers]);
  useEffect(() => { setData(null); load(); }, [load]);

  useEffect(() => {
    const sa = data?.sub_account;
    if (!sa || pool) return;
    if ((sa.my_roles || []).includes('admin') && sa.swish_choice_mode === 'admin_choice') {
      axios.get(`${API}/org/${orgId}/scope/swish-pool`, { headers })
        .then((r) => setPool(r.data.items || [])).catch(() => {});
    }
  }, [data, pool, orgId, headers]);

  if (err) return <p className="text-sm text-rose-600 py-8 text-center">{t.error_load}</p>;
  if (!data) return <p className="text-[13px] text-slate-400 py-8 text-center">{t.loading}</p>;

  const sa = data.sub_account;
  const roles = sa.my_roles || [];
  const isAdmin = roles.includes('admin');
  const canExport = isAdmin || roles.includes('revisor');
  const canPickSwish = isAdmin && sa.swish_choice_mode === 'admin_choice';
  const paymentUrl = sa.payment_url || (sa.paymig_id ? `${window.location.origin}/pay/${sa.paymig_id}` : '');

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: sa.name, url: paymentUrl });
      } else {
        await navigator.clipboard.writeText(paymentUrl);
        toast.success(t.copied);
      }
    } catch { /* user cancelled */ }
  };
  const copyLink = async () => {
    await navigator.clipboard.writeText(paymentUrl);
    toast.success(t.copied);
  };
  const dlFlyer = async () => {
    const bizLogo = data.business_logo_path ? `${API}/business/logo-file/${data.business_logo_path}` : '';
    await downloadFlyer(sa, {
      bizName: data.org_name || org.org_name || 'Business',
      bizLogo,
      paymentUrl,
      successMessage: t.flyer_downloaded,
    });
  };
  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/org/${orgId}/scope/sub-accounts/${subId}/export`, { headers });
      downloadCsv(res.data.csv_data, `paymig-${(sa.name || 'export').replace(/\s+/g, '-')}.csv`);
    } catch (e) { toast.error(wsErr(e)); }
  };

  const openNewEvent = () => { setForm({ name: '', description: '', default_message: '', fixed_amount: '', payment_mode: 'AMOUNT', line_items: [], require_guest_details: true }); setDialog('new_event'); };
  const openEdit = () => { setForm({
    name: sa.name || '', description: sa.description || '',
    default_message: sa.default_message || '',
    require_guest_details: sa.require_guest_details !== false,
    fixed_amount: sa.fixed_amount == null ? '' : String(sa.fixed_amount),
    payment_mode: sa.payment_mode || 'AMOUNT',
    line_items: (sa.line_items || []).map((li) => ({ description: li.description || '', amount: li.amount })),
  }); setDialog('edit'); };
  const openModerator = () => { setForm({ email: '', phone: '', name: '' }); setDialog('moderator'); };

  const submit = async () => {
    // Resolve AMOUNT vs LINE_ITEMS fields; returns null on validation error (toast already shown)
    const resolveModeFields = () => {
      if (form.payment_mode === 'LINE_ITEMS') {
        const items = (form.line_items || [])
          .filter((li) => (li.description || '').trim())
          .map((li) => ({ description: li.description.trim(), amount: parseFloat(li.amount) || 0 }));
        if (items.length === 0) { toast.error(t.line_items_required); return null; }
        return { payment_mode: 'LINE_ITEMS', line_items: items, fixed_amount: null };
      }
      const amt = form.fixed_amount === '' ? null : parseFloat(form.fixed_amount);
      if (amt !== null && (Number.isNaN(amt) || amt < 0)) { toast.error(t.invalid_amount); return null; }
      return { payment_mode: 'AMOUNT', fixed_amount: amt };
    };

    setSaving(true);
    try {
      if (dialog === 'new_event') {
        if (!form.name?.trim()) { toast.error(t.name_required); return; }
        const mode = resolveModeFields();
        if (!mode) return;
        const r = await axios.post(`${API}/org/${orgId}/scope/sub-accounts`,
          {
            name: form.name.trim(),
            description: form.description || null,
            parent_sub_account_id: subId,
            default_message: form.default_message || null,
            require_guest_details: form.require_guest_details !== false,
            ...mode,
          }, { headers });
        toast.success(t.event_created);
        // Optimistically prepend the newly created child so it appears
        // immediately without waiting for the next poll.
        if (r?.data?.id) {
          const newChild = {
            id: r.data.id,
            name: r.data.name || form.name.trim(),
            type: 'EVENT',
            total_amount: 0,
            tx_count: 0,
          };
          setData((d) => d ? { ...d, children: [newChild, ...(d.children || [])] } : d);
        }
      } else if (dialog === 'edit') {
        if (!form.name?.trim()) { toast.error(t.name_required); return; }
        const mode = resolveModeFields();
        if (!mode) return;
        await axios.put(`${API}/org/${orgId}/scope/sub-accounts/${subId}`,
          {
            name: form.name,
            description: form.description,
            default_message: form.default_message || null,
            require_guest_details: form.require_guest_details !== false,
            ...mode,
          }, { headers });
        toast.success(t.saved);
      } else if (dialog === 'moderator') {
        if (!form.email && !form.phone) { toast.error(t.email_or_phone_required); return; }
        await axios.post(`${API}/org/${orgId}/members`,
          { role: 'moderator', scope_id: subId, email: form.email || null, phone: form.phone || null, name: form.name || null },
          { headers });
        toast.success(t.invited);
      }
      setDialog(null);
      load();
    } catch (e) { toast.error(wsErr(e)); } finally { setSaving(false); }
  };

  const pickSwish = async (accountId) => {
    try {
      await axios.put(`${API}/org/${orgId}/scope/sub-accounts/${subId}/payout-swish`,
        { swish_account_id: accountId }, { headers });
      toast.success(t.saved);
      load();
    } catch (e) { toast.error(wsErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="ws-sub-detail">
      {/* Live tally */}
      <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="ws-live-tally">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold text-teal-700 mb-3">
          <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />{t.live_tally}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11.5px] text-slate-500">{t.total_collected}</div>
            <div className="text-[22px] font-semibold tabular-nums" data-testid="ws-tally-total">{fmtKr(data.tally.total)}</div>
            <div className="text-[11px] text-slate-400">{data.tally.count} {t.payments}</div>
          </div>
          <div>
            <div className="text-[11.5px] text-slate-500">{t.today}</div>
            <div className="text-[22px] font-semibold tabular-nums text-teal-700" data-testid="ws-tally-today">{fmtKr(data.tally.today_total)}</div>
            <div className="text-[11px] text-slate-400">{data.tally.today_count} {t.payments}</div>
          </div>
        </div>
      </section>

      {/* QR + share */}
      {paymentUrl && (
        <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="ws-qr-panel">
          <h3 className="text-[13px] font-semibold mb-3">{t.qr_title}</h3>
          <div className="flex items-center gap-4">
            <div className="p-2 border border-slate-100 rounded-lg bg-white shrink-0">
              <QRCodeSVG value={paymentUrl} size={124} fgColor="#134e4a" data-testid="ws-qr-code" />
            </div>
            <div className="flex-1 space-y-2 min-w-0">
              <div className="text-[11.5px] text-slate-400 tabular-nums truncate">{sa.display_paymig_id}</div>
              <Button size="sm" variant="outline" data-testid="ws-qr-share" className="w-full h-9 text-[13px] border-slate-200" onClick={share}>
                <Share2 size={14} className="mr-1.5" />{t.share}
              </Button>
              <Button size="sm" variant="outline" data-testid="ws-qr-copy" className="w-full h-9 text-[13px] border-slate-200" onClick={copyLink}>
                <Copy size={14} className="mr-1.5" />{t.copy_link}
              </Button>
              <Button size="sm" variant="outline" data-testid="ws-qr-flyer" className="w-full h-9 text-[13px] border-slate-200" onClick={dlFlyer}>
                <Printer size={14} className="mr-1.5" />{t.download_flyer}
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Admin tools */}
      {isAdmin && (
        <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-2" data-testid="ws-admin-tools">
          <h3 className="text-[13px] font-semibold">{t.admin_tools}</h3>
          <div className="grid grid-cols-2 gap-2">
            <Button size="sm" data-testid="ws-new-event" className="h-9 text-[12.5px] bg-teal-700 hover:bg-teal-800 text-white" onClick={openNewEvent}>
              <Plus size={14} className="mr-1" />{t.new_event}
            </Button>
            <Button size="sm" variant="outline" data-testid="ws-appoint-moderator" className="h-9 text-[12.5px] border-slate-200" onClick={openModerator}>
              <UserPlus size={14} className="mr-1" />{t.appoint_moderator}
            </Button>
            {sa.type === 'EVENT' && (
              <Button size="sm" variant="outline" data-testid="ws-edit-event" className="h-9 text-[12.5px] border-slate-200" onClick={openEdit}>
                <Pencil size={14} className="mr-1" />{t.edit}
              </Button>
            )}
            {canExport && (
              <Button size="sm" variant="outline" data-testid="ws-export" className="h-9 text-[12.5px] border-slate-200" onClick={exportCsv}>
                <Download size={14} className="mr-1" />{t.export_csv}
              </Button>
            )}
          </div>

          <div className="pt-2 border-t border-slate-100">
            <div className="text-[12px] text-slate-500 mb-1.5">{t.payout_swish}</div>
            {canPickSwish ? (
              <Select value={sa.swish_account_id || undefined} onValueChange={pickSwish}>
                <SelectTrigger data-testid="ws-payout-select" className="h-9 text-[13px] bg-white border-slate-200">
                  <SelectValue placeholder={sa.connected_swish_id || t.select_swish} />
                </SelectTrigger>
                <SelectContent>
                  {(pool || []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.display_name} · {a.merchant_number}{a.is_primary ? ` (${t.primary})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center gap-1.5 text-[13px] text-slate-500" data-testid="ws-payout-locked">
                <Lock size={13} className="text-slate-400" />
                {sa.connected_swish_id || '—'} · {t.payout_locked}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Moderator/revisor export shortcut when no admin tools shown */}
      {!isAdmin && canExport && (
        <Button variant="outline" data-testid="ws-export" className="w-full h-9 text-[13px] border-slate-200" onClick={exportCsv}>
          <Download size={14} className="mr-1.5" />{t.export_csv}
        </Button>
      )}

      {/* Nested events */}
      {(data.children || []).length > 0 && (
        <section data-testid="ws-children">
          <h3 className="text-[13px] font-semibold uppercase tracking-wide text-slate-400 mb-2">{t.nested}</h3>
          <div className="space-y-2">
            {data.children.map((c) => (
              <button key={c.id} data-testid={`ws-child-${c.id}`} onClick={() => onOpenSub({ id: c.id, name: c.name })}
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm flex items-center gap-2.5 text-left hover:border-teal-300 transition-colors">
                <span className="text-[13.5px] font-medium flex-1 truncate">{c.name}</span>
                <ChevronRight size={15} className="text-slate-300" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Registrations */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="ws-registrations">
        <h3 className="text-[13px] font-semibold px-4 pt-4 pb-2">{t.registrations}</h3>
        {(data.registrations || []).length === 0 ? (
          <p className="text-[12.5px] text-slate-400 px-4 pb-5 text-center">{t.no_registrations}</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.registrations.map((r, i) => (
              <div key={i} className="flex items-center gap-2.5 px-4 py-2.5">
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${r.is_guest ? 'bg-sky-50 text-sky-700' : 'bg-teal-50 text-teal-700'}`}>
                  {r.is_guest ? <QrCode size={13} /> : <CircleDollarSign size={13} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] text-slate-700 truncate">
                    {r.is_guest ? `${t.guest}: ` : ''}{r.name || '—'}
                  </div>
                  <div className="text-[11px] text-slate-400">{fmtDate(r.created_at, lang)}</div>
                </div>
                <span className="text-[13px] font-semibold tabular-nums text-teal-700 shrink-0">{fmtKr(r.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Dialogs */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="ws-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialog === 'new_event' ? t.new_event : dialog === 'edit' ? t.edit : t.appoint_moderator}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {dialog === 'new_event' ? t.new_event : dialog === 'edit' ? t.edit : t.appoint_moderator}
            </DialogDescription>
          </DialogHeader>
          {(dialog === 'new_event' || dialog === 'edit') && (
            <div className="space-y-3">
              <div><Label className="text-[12px]">{t.name}</Label>
                <Input data-testid="ws-form-name" value={form.name || ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div><Label className="text-[12px]">{t.description}</Label>
                <Input data-testid="ws-form-description" value={form.description || ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
              <div><Label className="text-[12px]">{t.default_message}</Label>
                <Input data-testid="ws-form-default-message" placeholder={t.default_message_hint}
                       value={form.default_message || ''}
                       onChange={(e) => setForm((f) => ({ ...f, default_message: e.target.value }))} /></div>
              <GuestDetailsSwitch checked={form.require_guest_details}
                label={t.require_guest_details} hint={t.require_guest_details_hint} testId="ws-form-require-guest-details"
                onChange={(v) => setForm((f) => ({ ...f, require_guest_details: v }))} />
              <div>
                <Label className="text-[12px]">{t.payment_type}</Label>
                <div className="flex gap-2 mt-1">
                  <button type="button" data-testid="ws-mode-amount"
                    onClick={() => setForm((f) => ({ ...f, payment_mode: 'AMOUNT' }))}
                    className={`flex-1 h-9 rounded-lg text-[12.5px] font-medium border transition-colors ${form.payment_mode !== 'LINE_ITEMS' ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-slate-600 border-slate-200'}`}>
                    {t.amount_mode}
                  </button>
                  <button type="button" data-testid="ws-mode-line-items"
                    onClick={() => setForm((f) => ({ ...f, payment_mode: 'LINE_ITEMS', line_items: (f.line_items || []).length ? f.line_items : [{ description: '', amount: '' }] }))}
                    className={`flex-1 h-9 rounded-lg text-[12.5px] font-medium border transition-colors ${form.payment_mode === 'LINE_ITEMS' ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-slate-600 border-slate-200'}`}>
                    {t.line_items_mode}
                  </button>
                </div>
              </div>
              {form.payment_mode !== 'LINE_ITEMS' ? (
                <div><Label className="text-[12px]">{t.fixed_amount} <span className="text-slate-400 text-[11px] font-normal">({t.optional})</span></Label>
                  <Input data-testid="ws-form-fixed-amount" inputMode="decimal" placeholder="0.00"
                         value={form.fixed_amount || ''}
                         onChange={(e) => setForm((f) => ({ ...f, fixed_amount: e.target.value.replace(/[^0-9.]/g, '') }))} /></div>
              ) : (
                <div className="space-y-2" data-testid="ws-line-items">
                  {(form.line_items || []).map((li, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input data-testid={`ws-item-desc-${idx}`} className="flex-1" placeholder={t.item_description}
                             value={li.description || ''}
                             onChange={(e) => setForm((f) => { const items = [...f.line_items]; items[idx] = { ...items[idx], description: e.target.value }; return { ...f, line_items: items }; })} />
                      <Input data-testid={`ws-item-amount-${idx}`} className="w-24" inputMode="decimal" placeholder={t.item_amount}
                             value={li.amount ?? ''}
                             onChange={(e) => setForm((f) => { const items = [...f.line_items]; items[idx] = { ...items[idx], amount: e.target.value.replace(/[^0-9.]/g, '') }; return { ...f, line_items: items }; })} />
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" data-testid={`ws-item-remove-${idx}`}
                              onClick={() => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }))}>
                        <Trash2 size={14} className="text-slate-400" />
                      </Button>
                    </div>
                  ))}
                  {(form.line_items || []).length < 4 ? (
                    <Button variant="outline" size="sm" className="h-8 text-[12px] border-slate-200" data-testid="ws-add-item"
                            onClick={() => setForm((f) => ({ ...f, line_items: [...(f.line_items || []), { description: '', amount: '' }] }))}>
                      <Plus size={13} className="mr-1" />{t.add_item}
                    </Button>
                  ) : (
                    <p className="text-[11px] text-slate-400">{t.max_items_reached}</p>
                  )}
                </div>
              )}
              <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
                <div className="text-[11px] text-slate-400 uppercase tracking-wide font-semibold">{t.payout_swish}</div>
                <div className="text-[12.5px] text-slate-700 mt-0.5 flex items-center gap-1.5">
                  <Lock size={11} className="text-slate-400" />
                  {sa.connected_swish_id || sa.swish_account_id || t.inherits_from_parent}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">{t.payout_locked_hint}</div>
              </div>
            </div>
          )}
          {dialog === 'moderator' && (
            <div className="space-y-3">
              <div><Label className="text-[12px]">{t.email}</Label>
                <Input data-testid="ws-mod-email" type="email" value={form.email || ''} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
              <div><Label className="text-[12px]">{t.phone}</Label>
                <Input data-testid="ws-mod-phone" value={form.phone || ''} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></div>
              <div><Label className="text-[12px]">{t.display_name}</Label>
                <Input data-testid="ws-mod-name" value={form.name || ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>{t.cancel}</Button>
            <Button data-testid="ws-dialog-submit" className="bg-teal-700 hover:bg-teal-800 text-white" disabled={saving} onClick={submit}>
              {dialog === 'moderator' ? (<><Send size={14} className="mr-1.5" />{t.send_invite}</>) : t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
